import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Register.css";

const API_BASE = "https://api.seritonex.com";

export default function Register({ onRegistered }) {
  // Read invite token from URL: /register?token=abc123
  const [token] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("token") || "";
  });

  const [tokenState, setTokenState] = useState("validating");
  const [tokenMeta, setTokenMeta] = useState(null);

  const [username, setUsername] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [emailReadOnly, setEmailReadOnly] = useState(false);
  const [inviteUserId, setInviteUserId] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [done, setDone] = useState(false);

  // ── Validate invite token on mount ──────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setTokenState("invalid");
      return;
    }

    // Decode JWT locally (no signature verification) and check payload.
    const decodeJwt = (t) => {
      try {
        const parts = t.split(".");
        if (parts.length !== 3) return null;
        // base64url -> base64
        const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const json = decodeURIComponent(
          atob(b64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join(""),
        );
        return JSON.parse(json);
      } catch (e) {
        return null;
      }
    };

    const payload = decodeJwt(token);
    if (!payload) {
      setTokenState("invalid");
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) {
      setTokenState("invalid");
      return;
    }

    setTokenMeta(payload || {});
    setTokenState("valid");
  }, [token]);

  // If token contains an email, lock it into the form; otherwise allow user input
  useEffect(() => {
    const tokenEmail =
      tokenMeta?.email ||
      tokenMeta?.email_address ||
      tokenMeta?.emailAddress ||
      tokenMeta?.email_address?.toString();
    if (tokenEmail) {
      setEmail(tokenEmail);
      setEmailReadOnly(true);
    } else {
      setEmail("");
      setEmailReadOnly(false);
    }
    // extract user id from token meta (common keys or any 'user' key)
    const extractUserId = (m) => {
      if (!m) return null;
      const candidates = ["userId", "user_id", "userid", "userID", "sub", "id"];
      for (const k of candidates) if (m[k] !== undefined) return Number(m[k]);
      for (const k of Object.keys(m)) {
        if (/user/i.test(k) && m[k] !== undefined && !isNaN(Number(m[k])))
          return Number(m[k]);
      }
      return null;
    };
    const uid = extractUserId(tokenMeta);
    setInviteUserId(uid);
  }, [tokenMeta]);

  // ── Password strength ────────────────────────────────────────────────────────
  const getStrength = (p) => {
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  };

  const strengthInfo = [
    null,
    { label: "Weak", color: "var(--danger)" },
    { label: "Fair", color: "#EF9F27" },
    { label: "Good", color: "#639922" },
    { label: "Strong", color: "var(--success)" },
  ];

  const strength = password.length ? getStrength(password) : 0;
  const strengthData = strengthInfo[strength];

  // ── Client-side validation ────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    // email required and basic format check
    if (!email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      errs.email = "Invalid email address.";
    // Username and surname are optional for token-based invites
    if (username && username.trim().length > 0 && username.trim().length < 3)
      errs.username = "At least 3 characters.";
    if (!password) errs.password = "Password is required.";
    else if (password.length < 8) errs.password = "At least 8 characters.";
    if (!confirm) errs.confirm = "Please confirm your password.";
    else if (confirm !== password) errs.confirm = "Passwords don't match.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError("");
    if (!validate()) return;

    setLoading(true);
    try {
      // Build request body expected by the registration endpoint
      const body = {
        email_address: email.trim(),
        secret: password,
        ...(inviteUserId ? { user_id: inviteUserId } : {}),
      };

      await axios.post(`${API_BASE}/api/v1/amis/user/register`, body, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      setDone(true);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Registration failed. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loading) handleSubmit();
  };

  // ── Render: validating ────────────────────────────────────────────────────────
  if (tokenState === "validating") {
    return (
      <div className="lp-root">
        <BrandPanel />
        <div className="lp-form-panel">
          <div className="lp-card">
            <div className="rg-loading">
              <span className="lp-spinner rg-spinner-lg" />
              <p className="rg-loading-text">Validating your invite…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: invalid token ─────────────────────────────────────────────────────
  if (tokenState === "invalid") {
    return (
      <div className="lp-root">
        <BrandPanel />
        <div className="lp-form-panel">
          <div className="lp-card">
            <div className="rg-status-icon rg-status-danger">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <h2
              className="lp-card-title"
              style={{ marginTop: 16, marginBottom: 8 }}
            >
              Invalid invite
            </h2>
            <p className="lp-card-sub">
              This invite link has expired or has already been used. Contact
              your administrator to request a new one.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: success ───────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="lp-root">
        <BrandPanel />
        <div className="lp-form-panel">
          <div className="lp-card">
            <div className="rg-status-icon rg-status-success">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2
              className="lp-card-title"
              style={{ marginTop: 16, marginBottom: 8 }}
            >
              Account Verified!
            </h2>
            <p className="lp-card-sub" style={{ marginBottom: 24 }}>
              Your account is ready. Head to the login page to sign in.
            </p>
            <button
              className="lp-btn"
              onClick={() => {
                // Navigate to login — works with React Router or plain redirect
                if (onRegistered) {
                  onRegistered();
                } else {
                  window.location.href = "/login";
                }
              }}
            >
              Go to login →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: registration form ─────────────────────────────────────────────────
  return (
    <div className="lp-root">
      <BrandPanel />
      <div className="lp-form-panel">
        <div className="lp-card">
          {/* Header */}
          <div className="lp-card-header">
            <p className="lp-eyebrow">Invite accepted</p>
            <h2 className="lp-card-title">Verify account</h2>
            <p className="lp-card-sub">
              {tokenMeta?.email
                ? `Setting up access for ${tokenMeta.email}`
                : "Pick a username and password to get started."}
            </p>
          </div>

          {/* Invite token indicator */}
          <div className="rg-token-bar">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span className="rg-token-text">Invite token verified</span>
            <span className="rg-token-code">
              {token.length > 10 ? `${token.slice(0, 8)}…` : token}
            </span>
          </div>

          {/* API error */}
          {error && (
            <div className="lp-error" role="alert">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}

          {/* Form */}
          <div className="lp-form">
            {/* Email (auto-filled from token, read-only if present) */}
            <div className="lp-field">
              <label className="fl" htmlFor="reg-email">
                Email address
              </label>
              <input
                id="reg-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`fi${fieldErrors.email ? " fi-error" : ""}`}
                autoComplete="email"
                onKeyDown={handleKeyDown}
                readOnly={emailReadOnly}
                disabled={emailReadOnly}
              />
              {emailReadOnly && (
                <div className="rg-help">Email supplied by invite — locked</div>
              )}
              {fieldErrors.email && (
                <span className="rg-field-error">{fieldErrors.email}</span>
              )}
            </div>

            {/* Password */}
            <div className="lp-field">
              <label className="fl" htmlFor="reg-password">
                Password
              </label>
              <div className="lp-pw-wrap">
                <input
                  id="reg-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`fi lp-pw-input${fieldErrors.password ? " fi-error" : ""}`}
                  autoComplete="new-password"
                  onKeyDown={handleKeyDown}
                />
                <button
                  type="button"
                  className="lp-pw-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? (
                    <i class="bi bi-eye-slash"></i>
                  ) : (
                    <i class="bi bi-eye"></i>
                  )}
                </button>
              </div>

              {/* Strength meter */}
              {password.length > 0 && (
                <div className="rg-strength">
                  <div className="rg-strength-track">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="rg-strength-seg"
                        style={{
                          background:
                            i <= strength
                              ? strengthData?.color
                              : "var(--border)",
                        }}
                      />
                    ))}
                  </div>
                  {strengthData && (
                    <span
                      className="rg-strength-label"
                      style={{ color: strengthData.color }}
                    >
                      {strengthData.label}
                    </span>
                  )}
                </div>
              )}

              {fieldErrors.password && (
                <span className="rg-field-error">{fieldErrors.password}</span>
              )}
            </div>

            {/* Confirm password */}
            <div className="lp-field">
              <label className="fl" htmlFor="reg-confirm">
                Confirm password
              </label>
              <div className="lp-pw-wrap">
                <input
                  id="reg-confirm"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Re-enter password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={`fi lp-pw-input${fieldErrors.confirm ? " fi-error" : ""}`}
                  autoComplete="new-password"
                  onKeyDown={handleKeyDown}
                />
                <button
                  type="button"
                  className="lp-pw-toggle"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  onClick={() => setShowConfirm((v) => !v)}
                >
                  {showConfirm ? (
                    <i class="bi bi-eye-slash"></i>
                  ) : (
                    <i class="bi bi-eye"></i>
                  )}
                </button>
              </div>
              {fieldErrors.confirm && (
                <span className="rg-field-error">{fieldErrors.confirm}</span>
              )}
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="lp-btn"
            >
              {loading && <span className="lp-spinner" />}
              {loading ? "Verifying account…" : "Verify account"}
            </button>
          </div>

          {/* Link back to login */}
          <div className="lp-switch" style={{ marginTop: 20 }}>
            <span className="lp-switch-text">Already have an account?</span>
            <button
              className="lp-switch-btn"
              onClick={() => {
                if (onRegistered) onRegistered();
                else window.location.href = "/login";
              }}
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Shared brand panel (mirrors Login.jsx left side) ──────────────────────────
function BrandPanel() {
  return (
    <div className="lp-brand">
      <div className="lp-brand-glow" />
      <h1 className="lp-brand-heading">
       Beta CRM.
        <br />
        <span>Built to perform.</span>
      </h1>
      <div className="lp-brand-stats">
        <div className="lp-stat">
          <span className="lp-stat-val">Order</span>
          <span className="lp-stat-lbl">Real Time Quotes</span>
        </div>
        <div className="lp-stat">
          <span className="lp-stat-val">Live</span>
          <span className="lp-stat-lbl">Reporting</span>
        </div>
        <div className="lp-stat">
          <span className="lp-stat-val">Track</span>
          <span className="lp-stat-lbl">Live Tracking on active Gas</span>
        </div>
      </div>
    </div>
  );
}
